import { Box, Typography, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { colors } from "../../theme/palette";

import HotInsights from "./HotInsights";
import NewFollowers from "./NewFollowers";
import Interactions from "./Interactions";
import HumanResourcesV2 from "./HumanResourcesV2";
import { useGetCurrentPlan } from "../../features/Plans/api/queries/useGetCurrentPlan";
import APP_ROUTES from "../../routers/routeConstants";
import JobsSummary from "../../features/GoHire/pages/GoHirePage/JobsOverviewPage/JobsSummary";

const Dashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: currentPlanData } = useGetCurrentPlan();

  const plan = currentPlanData?.data?.data?.plan;
  // Support both structures: new nested (plan.subscriptionType) and old flat (plan)
  const currentPlan = plan?.subscriptionType || plan;

  return (
    <Box sx={styles.container}>
      <Box sx={styles.headerContainer}>
        <Box sx={styles.titleContainer}>
          <Typography variant="h1ExtraBold" sx={styles.title}>
            {t("pages.dashboard.professionalDashboard")}
          </Typography>
          <Typography variant="body16Regular" sx={styles.description}>
            {t("pages.dashboard.howCanGoalngoHelpYouToday")}
          </Typography>
        </Box>
        {currentPlan && (
          <Box sx={styles.currentPlanContainer}>
            <Box sx={styles.currentPlanInfo}>
              <Box sx={styles.currentPlanText}>
                <Box sx={styles.currentPlanTitleRow}>
                  <Typography sx={styles.currentPlanTitle}>{currentPlan.title}</Typography>
                  {currentPlan.isFree && (
                    <Typography sx={styles.currentPlanFreeBadge}>{t("plans:page.plansOverviewPage.free")}</Typography>
                  )}
                </Box>
                {currentPlan.description && (
                  <Typography sx={styles.currentPlanDescription}>{currentPlan.description}</Typography>
                )}
              </Box>
              <Button variant="contained" onClick={() => navigate(APP_ROUTES.PLANS)} sx={styles.upgradeButton}>
                {t("plans:page.plansOverviewPage.upgrade") || "Upgrade"}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
      <JobsSummary />
      <HotInsights />
      <Box sx={styles.newFollowersAndInteractions}>
        <NewFollowers />
        <Interactions />
      </Box>
      <HumanResourcesV2 />
    </Box>
  );
};

export default Dashboard;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    position: "relative",
  },
  headerContainer: {
    display: "flex",
    flexDirection: { xs: "column", md: "row" },
    justifyContent: { xs: "flex-start", md: "space-between" },
    alignItems: { xs: "flex-start", md: "center" },
    rowGap: { xs: "16px", md: "0" },
  },
  titleContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  title: {
    color: colors.Black100,
    marginBottom: "6px",
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
  },
  description: {
    color: colors.Black100,
    marginBottom: "12px",
  },
  currentPlanContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "12px 16px",
    borderRadius: "12px",
    border: "1px solid #EAECF0",
    background: "#FFF",
  },
  currentPlanInfo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
  },
  currentPlanText: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  currentPlanTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  currentPlanTitle: {
    color: "var(--Gray-900, #101828)",
    fontFamily: "Montserrat",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "26px",
  },
  currentPlanDescription: {
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "20px",
    maxWidth: "300px",
  },
  currentPlanFreeBadge: {
    width: "fit-content",
    height: "fit-content",
    color: "var(--Brand-700, #076D0D)",
    textAlign: "center",
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",
    borderRadius: "16px",
    border: "1px solid var(--Brand-200, #98FF90)",
    background: "var(--Brand-50, #E7FFE4)",
    padding: "2px 8px",
  },
  upgradeButton: {
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "20px",
    textTransform: "none",
    whiteSpace: "nowrap",
  },
  newFollowersAndInteractions: {
    display: "grid",
    gridTemplateColumns: {
      xs: "repeat(1, 1fr)",
      md: "repeat(2, 1fr)",
    },
    gap: { xs: "16px", md: "10px" },
  },
};
